import moment from 'moment-timezone';

/**
 * The submission fields the Overview screen dates work off. Deliberately narrow: helpers here
 * are generic over the caller's own row shape so filtering keeps whatever else the API sent.
 */
export interface SubmissionTimes {
  created_at: string;
  updated_at: string;
  // Only COALESCEd by newer API builds, so both a missing key and a null have to be handled.
  sketched_at?: string | null;
  completed_at?: string | null;
}

// created_at / updated_at are *sync* times — students sketch offline and sync days later, so
// grading on them reads on-time work as late. sketched_at is when they first drew and
// completed_at when they last drew a correct attempt; older API builds can still send null or
// Go's zero time, hence the fallback.
const isUsableTime = (value?: string | null): value is string => (
  // Checked without String(value) - this runs twice per submission, and on a large course
  // that is 144,000 needless string allocations.
  typeof value === 'string' && value.length > 0 && !value.startsWith('0001-01-01')
);

export const firstSketchedAt = (sub: SubmissionTimes): string => (
  isUsableTime(sub.sketched_at) ? sub.sketched_at : sub.created_at
);

export const completedAt = (sub: SubmissionTimes): string => (
  isUsableTime(sub.completed_at) ? sub.completed_at : sub.updated_at
);

// Resolves a date picker's wall-clock time to an instant in the teacher's selected zone.
// Done once per report, not per submission: converting every submission into the zone
// instead was costing seconds on a large course.
const windowInstant = (picked: Date, timezone: string): number => moment.tz([
  picked.getFullYear(), picked.getMonth(), picked.getDate(),
  picked.getHours(), picked.getMinutes(), picked.getSeconds(),
], timezone).valueOf();

export interface TimeWindow {
  timezone: string;
  startDate: Date;
  endDate: Date;
}

/**
 * Keeps the submissions whose work overlaps the window the teacher picked — either the first
 * sketch or the completing one falling inside it, so work started before the window but
 * finished inside it still counts.
 */
export const filterSubmissionsByTime = <T extends SubmissionTimes>(
  submissions: T[],
  { timezone, startDate, endDate }: TimeWindow,
): T[] => {
  const from = windowInstant(startDate, timezone);
  const to = windowInstant(endDate, timezone);
  return submissions.filter((sub) => {
    const first = Date.parse(firstSketchedAt(sub));
    const completed = Date.parse(completedAt(sub));
    return (first >= from && first <= to) || (completed >= from && completed <= to);
  });
};

/* -------------------------------------------------------------------------
 * Stars
 * ---------------------------------------------------------------------- */

// A submission as the star maths sees it: correct, minus whatever help was taken.
export interface StarSubmission {
  is_correct?: boolean;
  did_peek?: boolean;
  did_look_at_hint?: boolean;
}

// '' rather than 0 for an incorrect submission: the table renders it as a blank cell.
export const getStars = (submission?: StarSubmission | null): '' | 1 | 2 | 3 | undefined => {
  if (!submission?.is_correct) return '';
  if (submission.is_correct) {
    if (submission.did_peek) return 1;
    if (submission.did_look_at_hint) return 2;
    if (!submission.did_peek && !submission.did_look_at_hint) return 3;
  }
};

export const calculateTotalStars = (submissions?: StarSubmission[] | null): number => (
  submissions?.reduce((acc, submission) => {
    if (!submission.is_correct) return acc;
    if (submission.did_peek) return acc + 1;
    if (submission.did_look_at_hint) return acc + 2;
    return acc + 3;
  }, 0) ?? 0
);

const COLOR_THRESHOLDS = [
  { minValue: 1, color: '#D3A2A3' },
  { minValue: 2.56, color: '#FFF9A4' },
  { minValue: 2.79, color: 'transparent' },
];

export const getColorForAverageStars = (value: number): string => COLOR_THRESHOLDS.reduce(
  (acc, threshold) => {
    if (value > threshold.minValue) {
      return threshold.color;
    }
    return acc;
  }, 'transparent',
);

/* -------------------------------------------------------------------------
 * Persistence
 * ---------------------------------------------------------------------- */

// Per-lesson percentages. '2D3D' is quoted because that is the key this object is built
// with, even though the same lesson is spelled _2d3d elsewhere in the download payloads.
export interface LessonPercentages {
  intro?: number;
  rot?: number;
  iso?: number;
  ortho?: number;
  '2D3D'?: number;
  slopes?: number;
  flat?: number;
  rot1?: number;
  rot2?: number;
  assembly?: number;
}

export const calculatePersistenceAverage = (percentages: LessonPercentages): number => {
  const validPercentages = [
    percentages?.intro,
    percentages?.rot,
    percentages?.iso,
    percentages?.ortho,
    percentages['2D3D'],
    percentages?.slopes,
    percentages?.flat,
    percentages?.rot1,
    percentages?.rot2,
    percentages?.assembly].filter((val): val is number => (val !== undefined));

  const sum = validPercentages.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  return sum / validPercentages.length;
};

/* -------------------------------------------------------------------------
 * Misc
 * ---------------------------------------------------------------------- */

export const getUserIdString = (users: { id: number }[]): string => {
  let userIdString = '';
  users.forEach((student, index) => {
    if (index === users.length - 1) userIdString += student.id;
    else userIdString += `${student.id}_`;
  });
  return userIdString;
};

export interface NamedPerson {
  first_name: string;
  last_name: string;
}

// Sorts in place, the way Array.prototype.sort does - callers use the returned array.
export const sortByLastName = <T extends NamedPerson>(info: T[]): T[] => info.sort((a, b) => {
  const aName = a.last_name.toLowerCase() + a.first_name.toLowerCase();
  const bName = b.last_name.toLowerCase() + b.first_name.toLowerCase();
  return aName.localeCompare(bName);
});

// Reads a date in the given zone and rebuilds it as a local Date. Overlaps toSelectedZone
// above but via Intl formatting rather than moment; kept separate because they disagree on
// sub-second precision and the date pickers depend on this one's exact output.
export const convertTZ = (date: string | Date, tzString: string): Date => new Date(
  (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', { timeZone: tzString }),
);

/**
 * Totals the stars each lesson earned, keyed the same way the caller keyed its input.
 *
 * Iterates Object.keys rather than the object itself: submissionsByLesson is a plain object
 * (`{ intro: [], rot: [], … }`), so calling .forEach on it directly throws TypeError before a
 * single star is counted - which is what this did until it was fixed.
 */
export const calculateStarsPerLesson = (
  submissionsByLesson: Record<string, StarSubmission[]>,
): Record<string, number> => {
  const starsPerLesson: Record<string, number> = {
    intro: 0,
    rot: 0,
    iso: 0,
    ortho: 0,
    _2d3d: 0,
    slopes: 0,
    flat: 0,
    rot1: 0,
    rot2: 0,
    assembly: 0,
  };
  Object.keys(submissionsByLesson).forEach((lesson) => {
    starsPerLesson[lesson] = calculateTotalStars(submissionsByLesson[lesson]);
  });
  return starsPerLesson;
};
